import { Component } from '@theme/component';

/**
 * A grid of selectable style tiles.
 *
 * Owns which of its own tiles is picked and nothing else. It does not know what a pair is,
 * which step it sits in, or whether a tab is hiding it — the selection above it decides what
 * a pick means, and hears about it through `product-builder-styles:change`.
 *
 * The tiles carry the style's data as attributes, so this component reads no metaobjects and
 * fetches nothing. `product-builder-icon-item.liquid` is where they come from.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [iconItem] - Style tiles, in order.
 *
 * @extends {Component<Refs>}
 */
class ProductBuilderStyles extends Component {
  /** @returns {HTMLElement[]} This grid's tiles, in order. */
  get tiles() {
    return this.refs.iconItem ?? [];
  }

  /** @returns {HTMLElement | null} The tile picked in this grid. */
  get selected() {
    return this.tiles.find((tile) => tile.classList.contains('is-selected')) ?? null;
  }

  /**
   * @returns {HTMLElement | null} What this grid shows before anything is picked: its first
   * tile. Used by a size grid or size guide beside it, so neither stands empty while the
   * shopper is still deciding.
   */
  get fallback() {
    return this.selected ?? this.tiles[0] ?? null;
  }

  /**
   * Called by the on:click binding on each tile.
   * @param {MouseEvent} event
   */
  selectIcon(event) {
    const tile = /** @type {HTMLElement | null} */ (
      /** @type {HTMLElement} */ (event.target).closest('.product-builder__icon-item')
    );

    if (tile) this.select(tile, true);
  }

  /**
   * Pick one tile, exclusive within this grid only. Another grid's pick is another
   * category's, and clearing across grids would take a sibling tab's selection with it.
   *
   * @param {HTMLElement} tile
   * @param {boolean} [picked] - True where a shopper chose it, rather than a url or a default
   *   naming it. What follows a pick may move the page, which is only welcome when someone
   *   asked for it.
   */
  select(tile, picked = false) {
    if (!this.tiles.includes(tile)) return;

    for (const sibling of this.tiles) {
      const selected = sibling === tile;
      sibling.classList.toggle('is-selected', selected);
      sibling.setAttribute('aria-pressed', selected ? 'true' : 'false');
    }

    this.#emit(picked);
  }

  /**
   * Pick the tile whose style handle matches, for a `?style=` restored from the url.
   *
   * @param {string} handle
   * @returns {boolean} Whether this grid had it.
   */
  selectByHandle(handle) {
    const tile = this.tiles.find((item) => item.dataset.style === handle);
    if (!tile) return false;

    this.select(tile);

    return true;
  }

  /** Drop this grid's pick, when another category in the same selection took it. */
  clear() {
    if (!this.selected) return;

    for (const tile of this.tiles) {
      tile.classList.remove('is-selected');
      tile.setAttribute('aria-pressed', 'false');
    }
  }

  /** @param {boolean} [picked] - Whether a shopper chose this, rather than a url or a default. */
  #emit(picked = false) {
    this.dispatchEvent(
      new CustomEvent('product-builder-styles:change', { bubbles: true, detail: { tile: this.selected, picked } })
    );
  }
}

if (!customElements.get('product-builder-styles')) {
  customElements.define('product-builder-styles', ProductBuilderStyles);
}
